/**
 * The block dialog's two scopes (cpanel `TableActions` "Для заказа" /
 * "Батарея"): `order` blocks order assignment, `box` blocks the battery box.
 */
export interface BlockChecks {
  order: boolean;
  box: boolean;
}

export type BlockScope = keyof BlockChecks;

/** A row counts as blocked when EITHER scope is blocked (legacy `isBlocked`). */
export function isCourierBlocked(row: {
  is_ibox_blocked?: boolean | null | undefined;
  is_order_blocked?: boolean | null | undefined;
}): boolean {
  return row.is_ibox_blocked === true || row.is_order_blocked === true;
}

/** Checkboxes mirror the courier's current block flags when the dialog opens. */
export function initialBlockChecks(row: {
  is_ibox_blocked?: boolean | null | undefined;
  is_order_blocked?: boolean | null | undefined;
}): BlockChecks {
  return { order: Boolean(row.is_order_blocked), box: Boolean(row.is_ibox_blocked) };
}

/**
 * Which scopes the current user may block (`couriers.block_orders` /
 * `couriers.block_box`). Passed in by the caller — an entity can't read the
 * access slice. Legacy role groups map onto it: ADMIN / MANAGER both,
 * CALL_CENTER / B2B_FRONT order only, AF_* / IBOX_MANAGER box only.
 */
export type BlockScopeAccess = BlockChecks;

/**
 * Scope toggling rules (cpanel `TableActions.handleChange`):
 * - nothing changes while the courier is already blocked (unblock flow);
 * - with BOTH scopes allowed, each toggles freely but the last one can never
 *   be unchecked;
 * - with ONE scope allowed, picking it forces that scope only, and clicks on
 *   the other scope are ignored;
 * - with neither allowed, nothing changes.
 */
export function nextBlockChecks(args: {
  allowed: BlockScopeAccess;
  checks: BlockChecks;
  scope: BlockScope;
  checked: boolean;
  isBlocked: boolean;
}): BlockChecks {
  const { allowed, checks, scope, checked, isBlocked } = args;
  if (isBlocked || !allowed[scope]) return checks;

  const other: BlockScope = scope === 'order' ? 'box' : 'order';
  if (allowed[other]) {
    if (!checked && !checks[other]) return checks;
    return { ...checks, [scope]: checked };
  }
  return { order: scope === 'order', box: scope === 'box' };
}

/** Block needs at least one scope; unblock never does (legacy `openBlockDialog`). */
export function canSubmitBlock(checks: BlockChecks, isBlocked: boolean): boolean {
  return isBlocked || checks.order || checks.box;
}
